import {createContext, useCallback, useContext, useEffect, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {availableRoutes} from '@/router/availableRoutes';
import {match} from 'path-to-regexp';
import {useFeaturesConfig} from './FeaturesContext';
import {useAppSelector} from '@/store/hooks.js';

const AuthContext = createContext(undefined);

export const useAuthContext = () => useContext(AuthContext);

export const AuthProvider = ({children}) => {

    const {isFeatureEnabled} = useFeaturesConfig();

    const roles = useAppSelector((state) => state.auth.roles);

    const location = useLocation();
    const navigate = useNavigate();

    // Vue courante : USER_ ou ADMIN_
    const [viewAs, setViewAs] = useState(() => {
        const pathSegments = location.pathname.split('/');
        const isOnUserSpecificPath =
            pathSegments[1] === 'administration' ||
            pathSegments[1] === 'profitSharing' ||
            pathSegments[1] === 'companyValuation' ||
            pathSegments[1] === 'overflow';
        return isOnUserSpecificPath ? 'USER_' : 'ADMIN_';
    });

    const [role, setRole] = useState(() => {
        return viewAs === 'ADMIN_' ? 'admin' : 'user';
    });

    /** -----------------------------
     *  Gestion de viewAs (USER_ / ADMIN_)
     *  ----------------------------- */
    // Sur la route racine : fixer viewAs selon les rôles
    useEffect(() => {

        if (location.pathname !== '/') {
            return;
        }

        const hasAdminPrivileges = roles.some((role) => role.startsWith('ADMIN'));
        const hasUserPrivileges = roles.some((role) => role.startsWith('USER'));

        if (hasAdminPrivileges && viewAs !== 'ADMIN_') {
            setViewAs('ADMIN_');
        } else if (!hasAdminPrivileges && hasUserPrivileges && viewAs !== 'USER_') {
            setViewAs('USER_');
        }
    }, [roles, location.pathname, viewAs]);

    // En dehors de la racine : déduire viewAs à partir de l’URL
    useEffect(() => {

        if (location.pathname === '/') {
            return;
        }

        const pathSegments = location.pathname.split('/');

        const isOnUserSpecificPath =
            pathSegments[1] === 'administration' ||
            pathSegments[1] === 'profitSharing' ||
            pathSegments[1] === 'companyValuation' ||
            pathSegments[1] === 'overflow';

        const isOnAdminSpecificPath = pathSegments[1] === 'consultant' || pathSegments[1] === 'admin';

        if (isOnUserSpecificPath && viewAs !== 'USER_') {
            setViewAs('USER_');
        } else if (isOnAdminSpecificPath && viewAs !== 'ADMIN_') {
            setViewAs('ADMIN_');
        }
    }, [location.pathname, viewAs]);

    const handleSetViewAs = useCallback(
        (userView = false, adminView = false) => {
            let newViewAs = viewAs;

            if (userView) {
                newViewAs = 'USER_';
            } else if (adminView) {
                newViewAs = 'ADMIN_';
            } else {
                newViewAs = viewAs === 'USER_' ? 'ADMIN_' : 'USER_';
            }

            setViewAs(newViewAs);

            setTimeout(() => {
                if (newViewAs === 'USER_') {
                    navigate('/administration');
                } else {
                    navigate('/consultant');
                }
            }, 100);
        },
        [viewAs, navigate]
    );

    /** -----------------------------
     *  Helpers auth / rôles / privilèges
     *  ----------------------------- */

    const hasRoles = useCallback(
        (rolesList) => {
            if (!Array.isArray(rolesList)) {
                return roles.includes(rolesList);
            }
            return rolesList.some((role) => roles.includes(role));
        },
        [roles]
    );

    const hasPrivileges = useCallback(
        (prefix) => {

            if (Array.isArray(prefix)) {
                console.warn('hasPrivileges appelé avec un tableau, utilisez hasAtLeastOnePrivilegesIn');
                return prefix.some((p) => roles.includes(p));
            }

            if (!roles || roles.length === 0) {
                return false;
            }

            return roles.some(privilege =>
                typeof privilege === 'string' && privilege.startsWith(prefix)
            );
        },
        [roles]
    );

    const hasAtLeastOnePrivilegesIn = useCallback(
        (requiredPrivileges) => {
            if (!requiredPrivileges || requiredPrivileges.length === 0) {
                return true;
            }
            if (!roles || roles.length === 0) {
                return false;
            }

            return requiredPrivileges.some((privilege) => roles.includes(privilege));
        },
        [roles]
    );

    const fullPath = useCallback((relativePath) => {
        const currentPath = window.location.pathname;
        const currentDir = currentPath.substring(0, currentPath.lastIndexOf('/'));

        if (relativePath.startsWith('/')) {
            return relativePath;
        }

        return `${currentDir}/${relativePath}`.replace(/\/+/g, '/');
    }, []);

    /** -----------------------------
     *  Navigation protégée / routes
     *  ----------------------------- */

    const isBrowsingAllowedTo = useCallback(
        (url) => {

            if (roles.length === 0) {
                return false;
            }

            if (!url) {
                return false;
            }
            url = url.trim().replace(/"/g, '');
            if (url === '.') {
                const currentPath = window.location.pathname;
                url = currentPath.substring(0, currentPath.lastIndexOf('/'));
            }

            const route = availableRoutes?.find(route => {
                if (!route?.url) {
                    return false;
                }

                // Gestion spéciale pour la route catch-all
                if (route.url === '/*') {
                    return true;
                }

                try {
                    const matcher = match(route?.url, {decode: decodeURIComponent});
                    return matcher(url);
                } catch (error) {
                    console.error('Error matching route:', route.url, 'with url:', url, error);
                    return false;
                }
            });

            const isAllowed = route?.available && hasAtLeastOnePrivilegesIn(route?.requiredPrivileges) && !(route?.featureActivation?.some(feature => !isFeatureEnabled(feature)));
            console.log(`Browsing check for URL: ${url} => allowed: ${isAllowed}`, {route, roles});
            return isAllowed;
        },
        [hasAtLeastOnePrivilegesIn, isFeatureEnabled, roles]
    );

    const isAvailableRoute = useCallback((url) => {
        if (!url) {
            return false;
        }

        if (url === '.') {
            const currentPath = window.location.pathname;
            url = currentPath.substring(0, currentPath.lastIndexOf('/'));
        }

        const route = availableRoutes?.find((route) => {
            if (!route?.url) {
                return false;
            }

            if (route.url === '/*') {
                return true;
            }

            try {
                const matcher = match(route.url, {decode: decodeURIComponent});
                return matcher(url);
            } catch (error) {
                console.error('Error matching route:', route.url, 'with url:', url, error);
                return false;
            }
        });

        return route?.available === true;
    }, []);

    return (
        <AuthContext.Provider value={{
            role,
            hasRoles,
            hasPrivileges,
            hasAtLeastOnePrivilegesIn,
            isBrowsingAllowedTo,
            isAvailableRoute,
            handleSetViewAs,
            fullPath,
            viewAs
        }}>
            {children}
        </AuthContext.Provider>
    );
};
